type OutlineEntry = { title: string; kind: string; durationMs: number; itemCount: number };
type BreakConfig = { enabled: boolean; availableAfterMs: number; budgetMs: number };

function minutes(ms: number): string {
  const m = Math.round(ms / 60_000);
  return `${m} min`;
}

/**
 * Everything the candidate needs to know before the clock starts. The forfeit rule
 * in particular has to be unmissable here — nobody should discover it by losing.
 */
export default function InstructionsStep({
  positionTitle,
  codename,
  outline,
  reconnectGraceMs,
  breakConfig,
  onContinue,
  busy,
}: {
  positionTitle: string;
  codename: string;
  outline: OutlineEntry[];
  reconnectGraceMs: number;
  breakConfig: BreakConfig | null;
  onContinue: () => void;
  busy: boolean;
}) {
  // Summed from the outline rather than taken as a separate number: two copies
  // of the same fact are two chances to disagree, and this is the one the
  // candidate is about to be held to.
  const testMs = outline.reduce((sum, s) => sum + s.durationMs, 0);

  return (
    <div className="mx-auto max-w-2xl p-6">
      <p className="text-xs uppercase tracking-widest text-ink/50">{positionTitle}</p>
      <h1 className="mt-2 text-2xl font-semibold">Before you begin</h1>
      <p className="mt-1 text-sm text-ink/60">
        You are taking this test as <strong className="text-ink">{codename}</strong>.
      </p>

      <section className="mt-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink/60">Structure</h2>
        <ol className="mt-3 space-y-2">
          {outline.map((section, index) => (
            <li
              key={`${section.title}-${index}`}
              className="flex items-baseline justify-between gap-4 rounded-md border border-ink/10 bg-surface px-4 py-2.5 text-sm"
            >
              <span className="font-medium">{section.title}</span>
              <span className="shrink-0 text-ink/60">
                {minutes(section.durationMs)}
                {section.itemCount > 0 ? ` · ${section.itemCount} questions` : ''}
              </span>
            </li>
          ))}
        </ol>
        <p className="mt-3 text-sm text-ink/70">
          {outline.length} sections, {minutes(testMs)} of testing.
        </p>
      </section>

      {breakConfig?.enabled ? (
        <section className="mt-6 rounded-md border border-accent/40 bg-accent/5 p-4">
          <h2 className="text-sm font-semibold text-accent">Optional break</h2>
          <p className="mt-1 text-sm text-ink/80">
            After {minutes(breakConfig.availableAfterMs)} a break button appears. Taking it is
            optional and <strong>pauses your test timer for up to {minutes(breakConfig.budgetMs)}</strong>
            — you can resume early at any time, and you keep whatever time was left when you paused.
            You may take one break.
          </p>
        </section>
      ) : null}

      <section className="mt-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink/60">Timing rules</h2>
        <ul className="mt-3 space-y-2 text-sm text-ink/80">
          <li>Each section has its own countdown. Some questions also have their own limit.</li>
          <li>
            The test runs continuously. The only pause is the optional break
            {breakConfig?.enabled ? '' : ' (not available on this test)'}.
          </li>
          <li>You cannot go back to a question once you have moved on.</li>
          <li>When time runs out the test advances automatically, answered or not.</li>
          <li>Timing is kept by the server, so reloading the page will not gain you time.</li>
        </ul>
      </section>

      <section className="mt-6 rounded-md border-2 border-danger bg-danger/5 p-4">
        <h2 className="text-sm font-semibold text-danger">Closing this tab ends your test</h2>
        <p className="mt-1 text-sm text-ink/80">
          There is no resume. If you close the tab, navigate away, or lose your connection for more
          than {Math.round(reconnectGraceMs / 1000)} seconds, the test is{' '}
          <strong>forfeited</strong> and your link stops working. The deadlines keep running while
          you are away.
        </p>
        <p className="mt-2 text-sm text-ink/80">
          Before you start: close anything that might interrupt you, and make sure you have
          uninterrupted time.
        </p>
      </section>

      <button type="button" className="btn-primary mt-8" onClick={onContinue} disabled={busy}>
        I understand — continue
      </button>
    </div>
  );
}
